import { ScrollSmoother } from 'gsap/ScrollSmoother.js';

export default class Lightbox {
  constructor(element) {
    this.element = element;

    this.options = {
      transitionDuration: 250, // doit correspondre au SCSS
      // Images qui ne doivent jamais s'agrandir
      ignoreSelector: '[data-lightbox-ignore], .comparison-slider img',
      dragTolerance: 8, // px : au-delà, c'est un glissement, pas un clic
    };

    this.pointerStart = null;

    this.overlay = null;
    this.previousFocus = null;
    this.isOpen = false;

    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.init();
  }

  init() {
    // Délégation d'événement : un seul écouteur sur le conteneur.
    // Fonctionne aussi pour les images ajoutées plus tard
    // (ex.: slides dupliquées par Swiper).
    // Phase de capture (true) : on reçoit le clic AVANT les composantes
    // enfants (Swiper, etc.) qui pourraient appeler stopPropagation().
    this.element.addEventListener('click', this.onClick, true);

    // On mémorise où le doigt/la souris est posé pour distinguer un clic
    // d'un glissement (ex.: swipe dans le carrousel Swiper).
    this.element.addEventListener(
      'pointerdown',
      (event) => {
        this.pointerStart = { x: event.clientX, y: event.clientY };
      },
      true,
    );
  }

  wasDragged(event) {
    if (!this.pointerStart) return false;
    const dx = event.clientX - this.pointerStart.x;
    const dy = event.clientY - this.pointerStart.y;
    return Math.hypot(dx, dy) > this.options.dragTolerance;
  }

  onClick(event) {
    const image = event.target.closest('img');

    if (!image || !this.element.contains(image)) return;
    if (image.matches(this.options.ignoreSelector)) return;
    if (this.wasDragged(event)) return; // c'était un swipe, pas un clic

    event.preventDefault();
    this.open(image);
  }

  createOverlay(image) {
    const overlay = document.createElement('div');
    overlay.className = 'lightbox';
    // Styles de mise en page en ligne : l'overlay fonctionne même si le SCSS
    // n'est pas (encore) chargé. Le SCSS ajoute la transition d'apparition.
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '9998', // juste sous .cursor / .cursor-dot (9999)
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(8px)',
      webkitBackdropFilter: 'blur(8px)',
    });
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', image.alt || 'Image agrandie');

    overlay.innerHTML = `
      <div class="button lightbox-close" type="button">
        <div class="button-icon">
          <svg class="icon icon--md icon--circle">
            <use href="#icon-Exit"></use>
          </svg>
        </div>
        <div class="button-title">Fermer</div>
      </div>
      <img class="lightbox-image" alt="" />
    `;

    const bigImage = overlay.querySelector('.lightbox-image');
    bigImage.src = image.currentSrc || image.src;
    bigImage.alt = image.alt;
    Object.assign(bigImage.style, {
      // width/height à 100% : l'image est agrandie pour remplir l'écran
      // (max-width seul ne fait que réduire, jamais agrandir)
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    });

    const closeButton = overlay.querySelector('.lightbox-close');
    Object.assign(closeButton.style, {
      position: 'absolute',
      top: '1.5rem',
      right: '1.5rem',
      zIndex: '1',
    });

    // Un clic n'importe où (fond, image ou bouton) ferme le lightbox
    overlay.addEventListener('click', () => this.close());

    return overlay;
  }

  open(image) {
    if (this.isOpen) return;
    this.isOpen = true;

    this.previousFocus = document.activeElement;
    this.overlay = this.createOverlay(image);
    document.body.appendChild(this.overlay);

    this.lockScroll();
    document.addEventListener('keydown', this.onKeyDown);

    requestAnimationFrame(() => {
      this.overlay.classList.add('is-open');
      this.overlay
        .querySelector('.lightbox-close')
        .focus({ preventScroll: true });
    });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    const overlay = this.overlay;
    overlay.classList.remove('is-open');

    document.removeEventListener('keydown', this.onKeyDown);
    this.unlockScroll();

    setTimeout(() => overlay.remove(), this.options.transitionDuration);

    this.overlay = null;
    if (this.previousFocus) {
      this.previousFocus.focus?.({ preventScroll: true });
    }
  }

  // ScrollSmoother fait défiler la page avec un vrai scroll natif sur <html>.
  // Mettre overflow: hidden dessus remet donc la page tout en haut.
  // On met plutôt ScrollSmoother en pause.
  lockScroll() {
    this.smoother = ScrollSmoother.get();
    if (this.smoother) this.smoother.paused(true);
  }

  unlockScroll() {
    if (this.smoother) this.smoother.paused(false);
    this.smoother = null;
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }
}
